'use client';

import { getFragmentData } from '@/__generated__';
import { ArticleCardFragment } from '@/__generated__/graphql';
import { ENTRY_BASE_FRAGMENT } from '@/gql/fragments/entries.gql';
import { useBreakpointMax } from '@/theme';
import { parseUri } from '@liquorice/utils';
import { useTranslations } from 'next-intl';
import CtaButton from '../CtaButton';
import Image from '../Image';
import Box from '../ui/Box';
import Container from '../ui/Container';
import Flex from '../ui/Flex';
import Txt from '../ui/Txt';
import * as styles from './FeaturedCard.css';

type FeaturedArticleCardProps = { data: ArticleCardFragment | undefined };

const FeaturedArticleCard = ({ data }: FeaturedArticleCardProps) => {
  if (!data) return null;

  const { entryTitle, entryImage } = data;
  const t = useTranslations('common');
  const base = getFragmentData(ENTRY_BASE_FRAGMENT, data);
  const { uri, postDate } = base ?? {};
  const href = parseUri(uri);

  const md = useBreakpointMax('md');

  if (!href) return null;

  return (
    <Box cx={{ position: 'relative' }}>
      <Box className={styles.imageWrapper}>
        <Image className={styles.image} alt="featured-image" fixedRatio data={entryImage} />
      </Box>
      <Box className={styles.contentWrapper}>
        <Container maxWidth="xl" disablePadding={md}>
          <Box
            className={styles.content({ variant: 'default' })}
            cx={{ p: 'sm', mT: 'md' }}
            colorSet="eucalypt"
            paper>
            <Txt as="p" variant="h4">
              {entryTitle}
            </Txt>
            <Flex columnGap="xs" rowGap="xs" direction="row" justifyContent="between">
              <Txt as="span" uppercase cx={{ fontSize: 'small' }}>
                {postDate}
              </Txt>
              <CtaButton href={href} color="velvet" underline="always">
                {t('moreInfo')}
              </CtaButton>
            </Flex>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default FeaturedArticleCard;
